import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Empty, Modal, Timeline, Tooltip } from 'antd';
import moment from 'moment';
import {
  AppWindow,
  ExternalLink,
  Globe,
  Image as ImageIcon,
  Monitor,
  Server,
  Smartphone,
  Tablet,
} from 'lucide-react';

import Spinner from '../Spinner';

// =============================================================================
// ImpressionTimeline — shared primitive for the vertical timeline of
// impressions shown in expanded rows across Campaign Details (Sign Ups V2,
// V2 Transactions).
//
// Uses AntD's <Timeline> for the dotted-rail visual (keeps the classic
// timeline spine) and wraps each impression in a v2 capsule with:
//   · mono timestamp
//   · publisher block (thumb + name + optional "Visit" link)
//   · meta rows — Platform (auto device-icon) + optional Media (text)
//   · optional clickable creative thumb → preview Modal
//
// State slots baked in: `loading` → <Spin>, `error` → error message,
// empty `impressions` array → <Empty>. Consumers don't re-implement these.
//
// Emits the `.campaign-overview__impression-timeline-*` BEM scope from
// `_overview.scss`. See SPECIFICATION §10 consumer contract.
// =============================================================================

const DEFAULT_PLATFORM_ICON = (platform) => {
  const p = (platform || '').toLowerCase();
  if (p.includes('ipad') || p.includes('tablet')) return Tablet;
  if (p.includes('iphone') || p.includes('android') || p.includes('mobile')) return Smartphone;
  return Monitor;
};

// Application-type indicator icons. Driven by `applicationPlatform` string
// on each impression (e.g., "Application", "Website", "Ad Server"). Falls
// back to Globe for unknown labels.
const APP_PLATFORM_ICONS = {
  Application: AppWindow,
  Website: Globe,
  'Ad Server': Server,
};

const DEFAULT_DATE_FORMAT = 'MMM DD, YYYY · HH:mm:ss';

const ImpressionTimeline = ({
  impressions = [],
  loading = false,
  error = false,
  emptyText = 'No impressions',
  loadingText = 'Loading impressions…',
  errorText = 'Error loading impressions. Please try again.',
  sort = 'none',
  platformIconFor = DEFAULT_PLATFORM_ICON,
  showMediaRow = false,
  showCreativeThumb = false,
  dateFormat = DEFAULT_DATE_FORMAT,
  className,
}) => {
  const [modalImpression, setModalImpression] = useState(null);

  const sorted = useMemo(() => {
    if (sort === 'newest') {
      return [...impressions].sort(
        (a, b) => new Date(b.impressionTime) - new Date(a.impressionTime)
      );
    }
    if (sort === 'oldest') {
      return [...impressions].sort(
        (a, b) => new Date(a.impressionTime) - new Date(b.impressionTime)
      );
    }
    return impressions;
  }, [impressions, sort]);

  if (loading) {
    return (
      <div className="campaign-overview__impression-timeline-state">
        <Spinner size="sm" />
        <span style={{ marginLeft: 12 }}>{loadingText}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="campaign-overview__impression-timeline-state campaign-overview__impression-timeline-state--error">
        {errorText}
      </div>
    );
  }

  if (!sorted.length) {
    return (
      <div className="campaign-overview__impression-timeline-empty">
        <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  const extraClass = className ? ` ${className}` : '';

  return (
    <div className={`campaign-overview__impression-timeline${extraClass}`}>
      {showCreativeThumb && (
        <Modal
          title={modalImpression?.eventId || 'Impression'}
          visible={!!modalImpression}
          footer={null}
          onCancel={() => setModalImpression(null)}
        >
          {(modalImpression?.creativeUrl || modalImpression?.media) && (
            <img
              src={modalImpression.creativeUrl || modalImpression.media}
              alt={modalImpression.eventId}
              width="100%"
              style={{ borderRadius: 8 }}
            />
          )}
        </Modal>
      )}

      <Timeline>
        {sorted.map((imp, i) => {
          const {
            impressionTime,
            impressionPublisherPlatform,
            impressionPublisherUrl,
            impressionPublisher,
            impressionPublisherMedia,
            media,
            creativeUrl,
            eventId,
            platformTooltip,
            applicationPlatform,
            publishedBy,
          } = imp;

          const platform = impressionPublisherPlatform || 'Unknown';
          const publisher = impressionPublisher || 'Unknown publisher';
          const hasLink = impressionPublisherUrl && impressionPublisherUrl !== 'about:blank';
          const PlatformIcon = platformIconFor(platform);
          const AppPlatformIcon = applicationPlatform
            ? APP_PLATFORM_ICONS[applicationPlatform] || Globe
            : null;
          // `creativeUrl` is the explicit image URL field; `media` is the
          // text-descriptor field used by `showMediaRow`. For backwards-compat
          // with V2 consumers that historically put a URL into `media`,
          // fall back to `media` when `creativeUrl` isn't set.
          const thumbUrl = creativeUrl || media;
          const hasThumb = !!thumbUrl;

          return (
            <Timeline.Item key={eventId || i}>
              <div className="campaign-overview__impression-timeline-item">
                <div className="campaign-overview__impression-timeline-time">
                  {moment(impressionTime).format(dateFormat)}
                </div>

                <div className="campaign-overview__impression-timeline-body">
                  {showCreativeThumb && (
                    <div
                      className={
                        'campaign-overview__impression-timeline-creative' +
                        (hasThumb
                          ? ''
                          : ' campaign-overview__impression-timeline-creative--static')
                      }
                      onClick={() => hasThumb && setModalImpression(imp)}
                      role={hasThumb ? 'button' : undefined}
                      tabIndex={hasThumb ? 0 : undefined}
                      aria-label={hasThumb ? `Preview impression ${eventId || ''}` : undefined}
                      data-no-expand
                    >
                      <div
                        className="campaign-overview__creatives-thumb"
                        style={hasThumb ? { backgroundImage: `url("${thumbUrl}")` } : undefined}
                      >
                        {!hasThumb && (
                          <ImageIcon size={18} color="#94a3b8" strokeWidth={1.75} />
                        )}
                      </div>
                    </div>
                  )}

                  <div className="campaign-overview__impression-timeline-meta">
                    <div className="campaign-overview__impression-timeline-meta-row">
                      <span className="campaign-overview__impression-timeline-meta-label">
                        Platform
                      </span>
                      <Tooltip overlayClassName="mjl-ds" title={platformTooltip || platform}>
                        <span className="campaign-overview__impression-timeline-meta-value">
                          <PlatformIcon size={13} strokeWidth={1.75} style={{ marginRight: 6 }} />
                          {platform}
                        </span>
                      </Tooltip>
                    </div>
                  </div>

                  <div className="campaign-overview__impression-timeline-publisher">
                    <span className="campaign-overview__impression-timeline-publisher-label">
                      Served on
                    </span>
                    {impressionPublisherMedia && (
                      <div
                        className="campaign-overview__creatives-thumb"
                        style={{ backgroundImage: `url("${impressionPublisherMedia}")` }}
                      />
                    )}
                    <div className="campaign-overview__impression-timeline-publisher-text">
                      <Tooltip overlayClassName="mjl-ds" title={impressionPublisherUrl || publisher}>
                        <span className="campaign-overview__impression-timeline-publisher-name">
                          {publisher}
                        </span>
                      </Tooltip>
                      {showMediaRow && media && (
                        <Tooltip overlayClassName="mjl-ds" title={media}>
                          <span className="campaign-overview__impression-timeline-publisher-media">
                            {media}
                          </span>
                        </Tooltip>
                      )}
                      {(hasLink || applicationPlatform) && (
                        <div className="campaign-overview__impression-timeline-publisher-actions">
                          {hasLink && (
                            <a
                              href={impressionPublisherUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="campaign-overview__creatives-link campaign-overview__impression-timeline-link"
                            >
                              <ExternalLink size={12} strokeWidth={2} />
                              <span>Visit</span>
                            </a>
                          )}
                          {applicationPlatform && (
                            <span className="campaign-overview__impression-timeline-ad-view-value">
                              {AppPlatformIcon && (
                                <AppPlatformIcon size={13} strokeWidth={1.75} style={{ marginRight: 6 }} />
                              )}
                              {applicationPlatform}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {publishedBy && (
                    <div className="campaign-overview__impression-timeline-published-by">
                      <span className="campaign-overview__impression-timeline-published-by-eyebrow">
                        Published by
                      </span>
                      <div className="campaign-overview__impression-timeline-published-by-text">
                        <Tooltip overlayClassName="mjl-ds" title={publishedBy}>
                          <span className="campaign-overview__impression-timeline-published-by-value">
                            {publishedBy}
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Timeline.Item>
          );
        })}
      </Timeline>
    </div>
  );
};

ImpressionTimeline.propTypes = {
  impressions: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  emptyText: PropTypes.node,
  loadingText: PropTypes.node,
  errorText: PropTypes.node,
  sort: PropTypes.oneOf(['newest', 'oldest', 'none']),
  platformIconFor: PropTypes.func,
  showMediaRow: PropTypes.bool,
  showCreativeThumb: PropTypes.bool,
  dateFormat: PropTypes.string,
  className: PropTypes.string,
};

export default ImpressionTimeline;
